import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useDeleteVasanItems, useGetAllVasanItems } from '../hooks/hooks';
import "react-loading-skeleton/dist/skeleton.css";
import { showErrorToast, showSuccessToast } from '../components/common';
import AddvasanitemsModal from './../modal/AddvasanitemsModal'
import { debounce } from 'lodash';
import Header from '../components/Common/Header';
import { DeleteIcon, EditIcon, NDVasanI } from '../assets/icons/icon';
import NoDataFound from '../components/Common/NoDataFound';
import SearchControls from '../components/Common/SearchControls';
import Pagination from '../components/Common/Pagination';
import { usePagination } from '../hooks/usePagination';
import { ButtonWithIcon } from '../components/Common/Buttons';
import TableSkeleton from '../components/Common/TableSkeleton';

function VasanItems() {
  const { t } = useTranslation();
  const { data: allVasanItems, refetch, isLoading } = useGetAllVasanItems();
  const { mutate: deleteVasanItems } = useDeleteVasanItems();
  const [isPopupOpen, setPopupOpen] = useState(false);

  const [isEdit, setIsEdit] = useState(false);
  const [isEditItem, setIsEditItem] = useState(undefined);
  const [isDeleteVasanItems, setIsDeleteVasanItems] = useState(undefined);
  const [isOpenDelete, setisOpenDelete] = useState(false);

  const [allVasanList, setAllVasanList] = useState(allVasanItems);
  const [searchText, setSearchText] = useState('');
  const [filteredVasanList, setFilteredVasanList] = useState(allVasanItems);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const debouncedHandleSearch = useCallback(debounce(handleSearchChange, 1000), []);
  const allVasanRef = useRef(allVasanList);

  useEffect(() => {
    if (allVasanItems) {
      setAllVasanList(allVasanItems);
      setFilteredVasanList(allVasanItems)
      allVasanRef.current = allVasanItems
    }
  }, [allVasanItems]);

  function handleSearchChange(value) {
    if (!allVasanRef.current || !Array.isArray(allVasanRef.current)) {
      console.warn("allCategoryRef is not ready or not an array");
      return;
    }
    const searchValue = value.trim().toLowerCase();
    if (!searchValue) {
      setFilteredVasanList(allVasanRef.current);
      return;
    }

    const filtered = allVasanRef.current.filter((menu) =>
      menu.name.toLowerCase().includes(searchValue)
    );
    setFilteredVasanList(filtered);
    setCurrentPage(1); // Reset to first page when searching
  }

  const handleOpenPopup = () => {
    setPopupOpen(true);
  }

  const onDeleteClose = (isDelete) => {
    if (isDelete) {
      handleDeleteDairyItems(isDeleteVasanItems);
    }
    setisOpenDelete(false)
  }

  const handleDeleteDairyItems = (item) => {
    deleteVasanItems(item, {
      onSuccess: async (res) => {
        refetch();
        showSuccessToast(res?.data?.message);
      },
      onError: (err) => {
        showErrorToast(err.response.data.error)
      },
    });
  };

  const handleClosePopup = async (isRefresh) => {
    setPopupOpen(false);
    setIsEdit(false);
    setIsEditItem(undefined);
    if (isRefresh) {
      refetch();
    }
  }

  const getUnitTypes = (unitType) => {
    switch (unitType.toLowerCase()) {
      case 'gram':
        return 'ગ્રામ (Gram)';
      case 'kgs':
        return 'કિગ્રા (KGS)';
      case 'liter':
        return 'લિટર (Liter)';
      case 'ml':
        return 'મિલીલીટર (ML)';
      case 'pieces':
        return 'નંગ (Pieces)';
      case 'per_person':
        return 'માણસો દીઠ (Per Person)';
      default:
        return unitType;
    }
  }

  // Pagination calculations
  const totalItems = filteredVasanList?.length || 0;

  const {
    totalPages,
    startIndex,
    endIndex,
    pageNumbers,
    onPageChange,
    onItemsPerPageChange
  } = usePagination({
    totalItems,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage
  });

  const currentItems = filteredVasanList?.slice(startIndex, endIndex) || [];


  const VasanCard = ({ item, index }) => (
    <div className="bg-white rounded-xl shadow-card border border-gray-100 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 p-6">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {item.name}
          </h3>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            <span className="text-sm text-gray-600">{getUnitTypes(item.unitType)}</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <ButtonWithIcon title="Edit" variant='secondary' Icon={EditIcon} btnclass="p-[5px] text-green-600 hover:bg-green-50 rounded-md transition-all border border-green-300" iconClass="mr-0" onClick={(e) => {
            e.stopPropagation();
            setIsEdit(true);
            setIsEditItem(item);
            handleOpenPopup(true);
          }} />
          <ButtonWithIcon title="Edit" variant='secondary' Icon={DeleteIcon} iconClass="w-4 h-4 mr-0" btnclass="p-[5px] text-red-600 hover:bg-red-50 rounded-md border border-red-300" onClick={(e) => {
            e.stopPropagation();
            setIsDeleteVasanItems(item);
            setisOpenDelete(true);
          }} />
        </div>
      </div>
    </div>
  );

  const TableRow = ({ item, index }) => (
    <div
      className={`w-full flex py-4 font-medium text-sm text-gray-700 border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200
          ${index % 2 === 0 ? "bg-gray-50/50" : "bg-white"}`}
    >
      <div className="w-[10%] pl-6 flex items-center font-medium text-gray-500">{startIndex + index + 1}</div>
      <div className="w-[45%] flex items-center pr-4">
        <div className="font-semibold text-gray-900 truncate" title={item.name}>{item.name}</div>
      </div>
      <div className="w-[35%] flex items-center pr-4">
        <div className="text-gray-700 truncate" title={getUnitTypes(item.unitType)}>{getUnitTypes(item.unitType)}</div>
      </div>
      <div className="w-[10%] flex items-center">
        <div className="flex items-center gap-2">
          <ButtonWithIcon title="Edit" variant='secondary' Icon={EditIcon} btnclass="p-2 text-green-600 hover:bg-green-50 rounded-md transition-all border border-green-300" iconClass="mr-0" onClick={(e) => {
            e.stopPropagation();
            setIsEdit(true);
            setIsEditItem(item);
            handleOpenPopup(true);
          }} />
          <ButtonWithIcon title="Delete" variant='secondary' Icon={DeleteIcon} iconClass="w-4 h-4 mr-0" btnclass="p-2 text-red-600 hover:bg-red-50 rounded-md border border-red-300" onClick={(e) => {
            e.stopPropagation();
            setIsDeleteVasanItems(item);
            setisOpenDelete(true);
          }} />
        </div>
      </div>
    </div>
  );


  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <div className=" mx-auto px-6 py-6">
          {/* Header Section */}
          <Header title={t('VasanItems')} subTitle={t('manage_your_vasan_items_and_traditional_products')} buttonText={t('add_new_VasanItems')} handleClick={handleOpenPopup} />

          {/* Search and Controls Section */}
          <SearchControls
            searchText={searchText}
            onSearchChange={setSearchText}
            onSearchDebounced={debouncedHandleSearch}
            placeholder={t('search_Vasan_lbl')}
            viewMode={viewMode}
            onViewChange={setViewMode}
            totalItems={totalItems}
          />

          {/* Content Section */}
          <div className="bg-white rounded-xl shadow-card overflow-hidden">
            {isLoading ? (
              <TableSkeleton
                columns={[50, 200, 150, 80]}
              />
            ) : (
              <>
                {viewMode === 'table' ? (
                  <>
                    {/* Table Header */}
                    <div className="bg-gray-50 border-b border-gray-200">
                      <div className="flex py-4 px-6 text-sm font-semibold text-gray-700">
                        <div className="w-[10%]">#</div>
                        <div className="w-[45%]">{t('VasanItems_name')}</div>
                        <div className="w-[35%]">{t('unitType')}</div>
                        <div className="w-[10%]">{t('Action')}</div>
                      </div>
                    </div>

                    {/* Table Body */}
                    <div>
                      {currentItems && currentItems.length > 0 ? (
                        currentItems.map((item, index) => (
                          <TableRow key={index} item={item} index={index} />
                        ))
                      ) : (
                        <NoDataFound text='No vasan items found' desc={searchText ? 'Try adjusting your search terms' : 'Get started by adding your first vasan item'} icon={<NDVasanI />} showButton={!searchText} buttonText='Add Vasan Item' handleClick={handleOpenPopup} />
                      )}
                    </div>
                  </>
                ) : (
                  /* Grid View */
                  <div className="p-6">
                    {currentItems && currentItems.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {currentItems.map((item, index) => (
                          <VasanCard key={index} item={item} index={index} />
                        ))}
                      </div>
                    ) : (
                      <NoDataFound text='No vasan items found' desc={searchText ? 'Try adjusting your search terms' : 'Get started by adding your first vasan item'} icon={<NDVasanI />} showButton={!searchText} buttonText='Add Vasan Item' handleClick={handleOpenPopup} />
                    )}
                  </div>
                )}

                {/* Pagination */}
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  pageNumbers={pageNumbers}
                  itemsPerPage={itemsPerPage}
                  totalItems={totalItems}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  onPageChange={onPageChange}
                  onItemsPerPageChange={onItemsPerPageChange}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <AddvasanitemsModal
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
        isEdit={isEdit}
        isEditItem={isEditItem}
      />

      {isOpenDelete && (
        <div className="popup-overlay ">
          <div className="popup-content !w-[447px] !p-[22px]" onClick={(e) => e.stopPropagation()}>
            <div className='text-[15px] pt-[13px] font-medium text-[#000] w-full flex justify-center items-center flex-col'>
              <span>{t('Are_u_sure')}</span>
              <span>{t('delete_VasanItems_confirmation_msg')}</span>
            </div>

            <div className='flex items-center mt-[35px] gap-[9px]'>
              <div className='w-[50%]'>
                <button className='w-full h-[52px] rounded-[26px] bg-[#E5E5E5] text-[15px] font-[600] text-[#36367A]'
                  onClick={() => { onDeleteClose() }}>
                  {t('btnCancel')}
                </button>
              </div>
              <div className='w-[50%]'>
                <button className='w-full h-[52px] rounded-[26px] bg-[#FF5252] text-[15px] font-[600] text-white'
                  onClick={() => { onDeleteClose(true) }}>
                  {t('btnDelete')}
                </button>
              </div>
            </div>

          </div>
        </div>)}
    </>
  )
}

export default VasanItems